import React from "react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from "components/ui/sheet";
import { CHARGE_HEAD_GROUPS } from "../charge-heads";
import type { BranchCnDetailsRow } from "../api/api";

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    cn: BranchCnDetailsRow | null;
}

const formatINR = (value: number) =>
    value.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const ChargeBreakdownSheet: React.FC<Props> = ({ open, onOpenChange, cn }) => {
    const visibleGroups = React.useMemo(() => {
        if (!cn) return [];
        return CHARGE_HEAD_GROUPS
            .map(g => ({
                group: g.group,
                heads: g.heads
                    .map(h => ({ ...h, value: Number(cn[h.col] ?? 0) }))
                    .filter(h => h.value > 0),
            }))
            .filter(g => g.heads.length > 0);
    }, [cn]);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="right"
                className="sm:max-w-md w-full overflow-y-auto"
            >
                <SheetHeader>
                    <SheetTitle>
                        Charge Breakdown {cn ? `— ${cn.cn_number}` : ""}
                    </SheetTitle>
                    <SheetDescription>
                        {cn ? (
                            <>
                                Total <span className="font-semibold text-foreground">₹{formatINR(cn.value)}</span>
                            </>
                        ) : null}
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-6">
                    {visibleGroups.length === 0 ? (
                        <div className="text-sm text-muted-foreground">
                            No charge-head breakdown available for this CN.
                        </div>
                    ) : (
                        visibleGroups.map(group => (
                            <section key={group.group}>
                                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                                    {group.group}
                                </h4>
                                <div className="rounded-md border divide-y">
                                    {group.heads.map(head => (
                                        <div
                                            key={head.col}
                                            className="flex items-center justify-between px-3 py-2 text-sm"
                                        >
                                            <span className="text-foreground">{head.label}</span>
                                            <span className="font-medium tabular-nums">
                                                ₹{formatINR(head.value)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ))
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default ChargeBreakdownSheet;
